import React from 'react';
import { Upload, X, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { CATEGORIES, formatBytes, getFileMeta } from '../../../../../components/knowledge/KnowledgeUtils';
import { Dropdown } from '../../../../../../../shared/components/atoms/Dropdown';
import { Input } from '../../../../../../../shared/components/atoms/Input/Input';
import { Button } from '../../../../../../../shared/components/atoms/Button/Button';

export function UploadForm({ hookData, onCancel }: { hookData: any, onCancel: () => void }) {
  const {
    file, setFile, fileError, setFileError, title, setTitle, desc, setDesc,
    cat, setCat, selectedOrgId, setSelectedOrgId, orgSearch, setOrgSearch,
    uploading, drag, setDrag, fileRef, isSuperAdmin, isFetching, page,
    orgOptions, handleScrollEnd, pickFile, handleUpload
  } = hookData;

  return (
    <form onSubmit={handleUpload}>
      <div style={{ 
        borderRadius: '0', 
        padding: '24px', 
        border: '1px solid var(--admin-card-border)', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '24px', 
        backgroundColor: 'var(--admin-bg-container, #010204)' 
      }}>

        {/* Drop zone */}
        <div className="auth-input-container" style={{ gap: 0 }}>
          <label className="auth-input-label" style={{ pointerEvents: 'auto' }}>FILE *</label>
          <div
            onDragOver={e => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={e => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files[0]; if (f) pickFile(f); }}
            onClick={() => fileRef.current?.click()}
            style={{ 
              border: `1px dashed ${drag ? 'var(--admin-sidebar-logo-text)' : fileError ? '#EF4444' : file ? '#10B981' : 'var(--admin-card-border)'}`, 
              borderRadius: '0', 
              padding: '32px', 
              textAlign: 'center', 
              cursor: 'pointer', 
              backgroundColor: drag ? 'rgba(56,189,248,0.02)' : file ? 'rgba(16,185,129,0.02)' : 'rgba(255,255,255,0.01)', 
              transition: 'all 0.2s' 
            }}
          >
            <input ref={fileRef} type="file" style={{ display: 'none' }} accept=".pdf,.docx,.csv"
              onChange={e => { const f = e.target.files?.[0]; if (f) pickFile(f); e.target.value = ''; }} />

            {file ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
                <div style={{ width: '40px', height: '40px', borderRadius: '0', backgroundColor: `${getFileMeta(file.name).color}15`, border: `1px solid ${getFileMeta(file.name).color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: getFileMeta(file.name).color, flexShrink: 0 }}>
                  {getFileMeta(file.name).icon}
                </div>
                <div style={{ textAlign: 'left', minWidth: 0 }}>
                  <p style={{ color: 'var(--admin-card-text)', fontSize: '14px', fontWeight: 700, margin: 0, wordBreak: 'break-all' }}>{file.name}</p>
                  <p style={{ color: '#10B981', fontSize: '12px', fontWeight: 500, margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <CheckCircle2 size={12} /> {formatBytes(file.size)} · Ready
                  </p>
                </div>
                <button type="button" onClick={e => { e.stopPropagation(); setFile(null); setFileError(null); }}
                  style={{ marginLeft: 'auto', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '0', cursor: 'pointer', color: '#EF4444', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '28px', height: '28px', flexShrink: 0 }}>
                  <X size={13} />
                </button>
              </div>
            ) : (
              <>
                <div style={{ width: '40px', height: '40px', borderRadius: '0', background: 'rgba(56,189,248,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', color: 'var(--admin-sidebar-logo-text)' }}>
                  <Upload size={18} />
                </div>
                <p style={{ color: 'var(--admin-card-text)', fontSize: '14px', fontWeight: 700, margin: '0 0 6px' }}>Drag & drop or <span style={{ color: 'var(--admin-sidebar-logo-text)' }}>browse</span></p>
                <p style={{ color: 'var(--admin-card-subtext)', fontSize: '12px', fontWeight: 500, margin: '0 0 12px' }}>PDF, DOCX, or CSV - max 20 MB</p>
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                  {[{ l: 'PDF', c: '#EF4444' }, { l: 'DOCX', c: '#38BDF8' }, { l: 'CSV', c: '#10B981' }].map(t => (
                    <span key={t.l} style={{ fontSize: '11px', fontWeight: 800, padding: '3px 8px', borderRadius: '0', backgroundColor: `${t.c}15`, color: t.c, border: `1px solid ${t.c}30`, letterSpacing: '.04em' }}>{t.l}</span>
                  ))}
                </div>
              </>
            )}
          </div>
          {fileError && (
            <p style={{ color: '#EF4444', fontSize: '12px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '5px', margin: 0 }}>
              <AlertCircle size={12} /> {fileError}
            </p>
          )}
        </div>

        {/* Title */}
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <Input 
            label="Document Title *"
            placeholder="e.g. Employee Handbook 2025" 
            value={title} 
            onChange={e => setTitle(e.target.value)} 
            required 
            containerClassName="kb-flat-input"
          />
        </div>

        {/* Category + Description + Target Org */}
        <div style={{ display: 'grid', gridTemplateColumns: isSuperAdmin ? '1fr 1fr 1fr' : '1fr 1fr', gap: '16px', alignItems: 'flex-start' }}>
          <div className="auth-input-container" style={{ gap: 0 }}>
            <label className="auth-input-label" style={{ pointerEvents: 'auto' }}>CATEGORY</label>
            <Dropdown
              value={cat}
              onChange={setCat}
              placeholder="Select category"
              options={CATEGORIES.map(c => ({ value: c, label: c }))}
              className="kb-dropdown-tall"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Input 
              label="Description"
              placeholder="Brief description (optional)" 
              value={desc} 
              onChange={e => setDesc(e.target.value)} 
              containerClassName="kb-flat-input"
            />
          </div>
          {isSuperAdmin && (
            <div className="auth-input-container" style={{ gap: 0 }}>
              <label className="auth-input-label" style={{ pointerEvents: 'auto' }}>ORGANIZATION</label>
              <Dropdown
                value={selectedOrgId}
                onChange={setSelectedOrgId}
                options={orgOptions}
                placeholder="Select target organization"
                searchable={true}
                onSearchChange={setOrgSearch}
                onScrollEnd={handleScrollEnd}
                isLoadingMore={isFetching && page > 1}
                className="kb-dropdown-tall"
              />
            </div>
          )}
        </div>

        {/* Actions */}
        <div style={{ paddingTop: '24px', marginTop: '8px', borderTop: '1px solid var(--admin-card-border)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <Button 
            type="button" 
            variant="ghost" 
            onClick={onCancel}
            className="kb-flat-btn"
            style={{ border: '1px solid var(--admin-card-border)', backgroundColor: 'transparent', color: 'var(--admin-card-subtext)' }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="primary" 
            disabled={uploading || !file || !title.trim()}
            isLoading={uploading}
            leftIcon={<Upload size={15} />}
            className="kb-flat-btn kb-white-btn"
          >
            Upload
          </Button>
        </div>
      </div>
    </form>
  );
}
